import {
  cn,
  Button,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
} from '@wellesley/ui'
import { Flag, X } from 'lucide-react'
import { ChangeEvent, FC, useEffect, useRef } from 'react'
import { warningOptions } from './hooks/useSensitiveControl'
import { FormProvider, useForm } from 'react-hook-form'
import useHover from 'hooks/useHover'

export type SensitiveControlProps = {
  isSensitive: boolean
  contentWarning: string
  isOpenContentWarning: boolean
  isDisabled?: boolean
  toggleSensitive: (this: void) => void
  setContentWarning: (this: void, value: string) => void
  toggleOpenContentWarning: (this: void) => void
  isMobile?: boolean
}

const DELAY = 300
const MAX_LENGTH = 60

const SensitiveControl: FC<SensitiveControlProps> = ({
  isMobile = false,
  isDisabled = false,
  contentWarning,
  setContentWarning,
  isOpenContentWarning,
  toggleOpenContentWarning,
}) => {
  const wasOpenRef = useRef(isOpenContentWarning)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    setContentWarning(value)
  }

  useEffect(() => {
    const isOpening = isOpenContentWarning && !wasOpenRef.current
    wasOpenRef.current = isOpenContentWarning
    if (!isOpening) return

    const delayedFocus = window.setTimeout(() => {
      if (!inputRef.current) return
      inputRef.current.focus()
    }, DELAY)

    return () => {
      window.clearTimeout(delayedFocus)
    }
  }, [isOpenContentWarning])

  const methods = useForm()
  const [ref, isHovered, setHovered] = useHover<HTMLDivElement>()

  const handleSubmitWarning = () => {
    toggleOpenContentWarning()
  }

  const handleCrossClick = (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation()
    setContentWarning('')
    isOpenContentWarning && toggleOpenContentWarning()
    setHovered(false)
  }

  return (
    <FormProvider {...methods}>
      <div
        className={isOpenContentWarning ? 'flex-1' : ''}
        data-testid="sensitive-control"
      >
        <div
          className={cn(`duration-custom gap-xxs flex items-center`, {
            'flex-1': isOpenContentWarning,
            'w-full': isMobile,
          })}
        >
          <div
            ref={ref}
            data-testid="sensitive-control-toggler"
            onClick={isDisabled ? undefined : toggleOpenContentWarning}
            className={cn(
              'duration-custom h-m gap-xxs flex w-6 items-center transition-all',
              {
                'p-xxs w-16 rounded-[6px] bg-gray-100': contentWarning,
                'pointer-events-none opacity-50': isDisabled,
              },
            )}
          >
            <Flag
              size={24}
              strokeWidth={2}
              color={
                isDisabled
                  ? 'var(--gray-300)'
                  : isHovered || contentWarning
                    ? 'var(--blue-500)'
                    : 'var(--gray-500)'
              }
              className={cn('cursor-pointer', {
                'cursor-not-allowed': isDisabled,
              })}
            />
            {contentWarning && (
              <X
                size={16}
                color="var(--gray-400)"
                className="cursor-pointer"
                onClick={handleCrossClick}
              />
            )}
          </div>
          <div
            className={cn(
              'duration-custom relative flex-1 transition-opacity',
              isOpenContentWarning ? 'visible' : 'hidden',
            )}
          >
            <Input
              name="contentWarning"
              ref={inputRef}
              maxLength={MAX_LENGTH}
              value={contentWarning}
              onChange={handleInputChange}
              placeholder="Media warning reason"
              className={cn(
                'absolute left-[3px] top-[3px] z-[1] h-[42px] w-[calc(100%-40px)] rounded-sm !rounded-r-none !border-r-0 outline-none ring-0 focus-visible:ring-0 focus-visible:ring-offset-0',
                {
                  'duration-custom text-gray-400': !contentWarning,
                },
              )}
            />
            <Select
              onValueChange={setContentWarning}
              defaultValue={contentWarning}
            >
              <SelectTrigger className={cn('min-w-[265px] cursor-pointer')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="p-xxs z-[101] min-w-[265px]">
                <SelectGroup>
                  {warningOptions.map((option) => (
                    <SelectItem
                      key={option}
                      value={option || ''}
                      className="p-xxs min-h-10 cursor-pointer rounded-[6px] text-sm font-semibold text-gray-700 hover:bg-gray-50 [&>span:first-child]:hidden"
                    >
                      {option}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          {isOpenContentWarning && (
            <Button
              variant="primary"
              className="w-[80px]"
              disabled={!contentWarning}
              onClick={handleSubmitWarning}
              data-testid="content-warning-set-button"
            >
              Set
            </Button>
          )}
        </div>
      </div>
    </FormProvider>
  )
}

export default SensitiveControl
